import { Component } from '../lib/ComponentBase';
import { simpleComponent } from '../lib/ComponentHelpers';
import type { IntentionProvider, ProposalProvider, AttackPositionProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import type { Intention } from '../types/AI';
import type Action from '../lib/Action';
import type { Proposal } from '../lib/Proposal';
import type HexCoords from '../grid/HexCoords';
import HexPosition from './HexPosition';
import { ActionType } from '../lib/Action';
import Faction from './Faction';
import { ProposalType } from '../lib/Proposal';

/**
 * A reactive ability for stationary actors. It allows an actor to attack any adjacent
 * opponent who takes a 'TURN' or 'IDLE' action.
 */
class OmniBite extends Component implements IntentionProvider, ProposalProvider, AttackPositionProvider, DisplayProvider {
  damage: number;
  priority: number;

  constructor(data: { damage?: number, priority?: number } = {}) {
    super();
    this.damage = data.damage ?? 10;
    this.priority = data.priority ?? 20;
  }

  getIntention(entity: Entity, worldState: WorldState): Intention | null {
    const { occupiedCoords } = worldState;
    const selfPosition = entity.getComponent(HexPosition);
    const selfFaction = entity.getComponent(Faction);
    if (!selfPosition || !selfFaction) return null;

    for (const neighbor of selfPosition.coords.allNeighbors()) {
      const occupant = occupiedCoords.get(neighbor.key);
      if (occupant && occupant.id !== entity.id) {
        const occupantFaction = occupant.getComponent(Faction);
        if (occupantFaction && occupantFaction.name !== selfFaction.name) {
          // Found an adjacent enemy. Propose a high-priority ATTACK.
          // The details are empty because this is just a threat posture. The actual
          // targets will be determined in getProposedOutcome.
          return {
            priority: 50,
            type: ActionType.IDLE,
            details: {},
            sourceComponentName: 'OmniBite',
          };
        }
      }
    }

    return null;
  }

  getProposedOutcome(selfAction: Action): Proposal | null {
    // This ability only triggers for intentions generated by OmniBite itself.
    if (selfAction.sourceComponentName !== 'OmniBite') {
      return null;
    }

    const selfPosition = selfAction.entity.getComponent(HexPosition);
    if (!selfPosition) return null;

    const attackEffects = selfPosition.coords.allNeighbors().map(neighbor => ({
      type: 'ATTACK' as const,
      attackHex: neighbor,
      damage: this.damage,
      onMiss: [{
        type: 'MISS' as const,
        animationPlan: [[{
          type: 'ATTACK_JAB' as const,
          entityId: selfAction.entity.id,
          targetCoords: neighbor,
        }]]
      }],
      animationPlan: [[{
        type: 'ATTACK_JAB' as const,
        entityId: selfAction.entity.id,
        targetCoords: neighbor,
      }]],
      attackTiming: 'BOTH' as const,
    }));

    if (attackEffects.length === 0) {
      return null;
    }

    return {
      type: ProposalType.REPLACEMENT,
      priority: this.priority,
      effects: [{ type: 'IDLE' as const }, ...attackEffects],
    };
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:surrounded-eye', label: 'Omni Bite', description: 'Bites all adjacent hexes simultaneously.' };
  }

  getAttackPositions(entity: Entity, worldState: WorldState): PathfindingNode[] {
    const playerPos = worldState.playerPosition;
    if (!playerPos) return [];

    // Can attack from any adjacent position (all 6 neighbors), facing toward the player
    const positions: PathfindingNode[] = [];
    playerPos.coords.allNeighbors().forEach((coords: HexCoords) => {
      const bearing = coords.isNeighbor(playerPos.coords);
      if (bearing !== false) {
        positions.push({ coords, bearing, cost: 0 });
      }
    });
    return positions;
  }

  getAttackPattern(entity: Entity): HexCoords[] {
    const selfPos = entity.getComponent(HexPosition);
    if (!selfPos) return [];

    // OmniBite attacks all 6 adjacent hexes
    return selfPos.coords.allNeighbors();
  }
}

export default simpleComponent(OmniBite);
